/**
 * 通用文件上传。
 *
 * 抽出来是因为多处要用：新建合同、编辑合同、附件详情里重新上传，以及发票复印件。
 * 上传逻辑本身在 `api.upload()` 里已经封装好（FormData + 鉴权 + 进度），
 * 这里只负责交互与**前端的大小/类型预检**。
 *
 * 【大小上限来自 @ilc/shared】
 * 从前前端写 20MB、后端写 10MB，用户传 15MB 的 PDF 会被前端放行、后端拒绝，
 * 而且错误只在服务端。现在两端读的是同一个常量，改一处即同时生效。
 *
 * 【accept 与文案必须由调用方给】
 * 合同与发票允许的类型**刻意不同**（合同可传 Word，发票复印件只能是
 * PDF/图片）。写死成合同的默认值会让发票附件页出现"提示可以传 Word、
 * 传上去被后端拒绝"的自相矛盾。
 */

import { useState } from 'react';
import { Alert, Button, Progress, Space, Typography, Upload } from 'antd';
import { InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { CONTRACT_ACCEPT, MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from '@ilc/shared';
import { feedback } from '@/utils/feedback';

export interface FileUploaderProps {
  /** 执行上传；由调用方决定传不传业务 ID（新建合同流程是 TEMP 态） */
  upload: (file: File, onProgress: (percent: number) => void) => Promise<unknown>;
  /** 上传完成后回调 */
  onUploaded: (result: unknown) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
  /** 紧凑模式：只显示一个按钮，用于已有附件时的补充上传 */
  compact?: boolean;
  /** `<input accept>` 的扩展名列表，默认合同附件 */
  accept?: string;
  /** 拖拽区的标题，默认合同附件 */
  title?: string;
  /** 拖拽区的说明，默认合同附件 */
  description?: React.ReactNode;
  /**
   * 上传成功提示。默认沿用合同那套「已上传并解析」——
   * 发票附件在通道未启用时**并不会解析**，此时必须由调用方改成
   * 「已上传」，否则又是一次"看起来做过了"的假象。
   */
  successMessage?: string;
}

export function FileUploader({
  upload,
  onUploaded,
  disabled,
  hint,
  compact,
  accept = CONTRACT_ACCEPT,
  title = '点击或拖拽上传合同附件',
  description = null,
  successMessage = '附件已上传并解析',
}: FileUploaderProps): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState(0);

  const doUpload = async (file: File): Promise<void> => {
    setBusy(true);
    setPercent(0);
    try {
      const result = await upload(file, (p) => setPercent(Math.round(p)));
      onUploaded(result);
      feedback.success(successMessage);
    } catch (err) {
      feedback.error(err instanceof Error ? err.message : '上传失败');
    } finally {
      setBusy(false);
      setPercent(0);
    }
  };

  const props = {
    accept,
    multiple: false,
    showUploadList: false,
    disabled: disabled || busy,
    beforeUpload: (file: File): boolean | typeof Upload.LIST_IGNORE => {
      if (file.size > MAX_UPLOAD_BYTES) {
        feedback.error(`文件 ${(file.size / 1024 / 1024).toFixed(1)}MB 超过上限 ${MAX_UPLOAD_LABEL}`);
        return Upload.LIST_IGNORE;
      }
      if (file.size === 0) {
        feedback.error('文件为空');
        return Upload.LIST_IGNORE;
      }
      void doUpload(file);
      // 始终返回 false：由我们自己调 api.upload，不让 antd 再发一次请求
      return false;
    },
  };

  if (compact) {
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Upload {...props}>
          <Button icon={<ReloadOutlined />} loading={busy} disabled={disabled}>
            {busy ? `解析中 ${percent}%` : '上传附件'}
          </Button>
        </Upload>
        {busy && percent > 0 && percent < 100 ? (
          <Progress percent={percent} size="small" />
        ) : null}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Upload.Dragger {...props} style={{ padding: '8px 0' }}>
        <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
          <InboxOutlined />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 14 }}>
          {title}
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
          {description ?? (
            <>
              支持 PDF、Word（.docx）与图片，单个不超过 {MAX_UPLOAD_LABEL}。
              上传后会自动识别合同正文与表格，并**尝试**预填下列字段——请逐项核对后再保存。
            </>
          )}
        </p>
      </Upload.Dragger>

      {busy ? (
        <Alert
          type="info"
          showIcon
          message={`正在解析附件…${percent > 0 && percent < 100 ? ` 上传 ${percent}%` : ''}`}
          description="PDF 与 Word 为本地解析，通常 1 秒内完成；扫描件需走外部服务。"
        />
      ) : null}

      {hint ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Typography.Text>
      ) : null}
    </Space>
  );
}

export default FileUploader;
